/**
 * Grid map component
 * Displays job grid cells on a Leaflet map
 * Optimized for large numbers of cells (100k+)
 */

import { useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Rectangle, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import type { LatLngBoundsExpression, LatLngBounds } from 'leaflet';
import L from 'leaflet';
import { Empty, Spin, Alert } from 'antd';
import type { GridCell, GridCellStatus, Bounds } from '@/types/grid';
import { CELL_STATUS_COLORS, MAP_DEFAULTS } from '@/utils/constants';
import { boundsToLeafletBounds } from '@/utils/mapUtils';
import 'leaflet/dist/leaflet.css';

// Maximum cells to render for performance
const MAX_RENDERED_CELLS = 500;

interface GridMapProps {
  cells: GridCell[];
  bounds: Bounds;
  loading?: boolean;
  height?: number | string;
  onCellClick?: (cell: GridCell) => void;
  selectedCellId?: string;
}

/**
 * Component to fit map bounds
 */
function FitBounds({ bounds }: { bounds: LatLngBoundsExpression }) {
  const map = useMap();

  useMemo(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [map, bounds]);

  return null;
}

/**
 * Component to track map viewport for cell filtering
 */
function ViewportTracker({ onViewportChange }: { onViewportChange: (bounds: LatLngBounds | null) => void }) {
  useMapEvents({
    moveend: (e) => {
      onViewportChange(e.target.getBounds());
    },
    zoomend: (e) => {
      onViewportChange(e.target.getBounds());
    },
    load: (e) => {
      onViewportChange(e.target.getBounds());
    },
  });

  return null;
}

/**
 * Check if a cell intersects with viewport bounds
 */
function cellIntersectsViewport(cell: GridCell, viewport: LatLngBounds): boolean {
  if (!cell.bounds) return false;
  const cellBounds = L.latLngBounds(
    [cell.bounds.south, cell.bounds.west],
    [cell.bounds.north, cell.bounds.east]
  );
  return viewport.intersects(cellBounds);
}

/**
 * Priority score for cell rendering (higher = render first)
 * Active and recently changed cells are prioritized
 */
function getCellPriority(cell: GridCell): number {
  switch (cell.status) {
    case 'processing':
    case 'running':
    case 'in_progress':
      return 100; // Currently processing - always show
    case 'failed':
      return 90; // Errors are important
    case 'completed':
      return 50; // Completed cells
    case 'queued':
      return 30; // Waiting in queue
    case 'skipped':
      return 10; // Skipped cells
    case 'pending':
    default:
      return 1; // Pending cells lowest priority
  }
}

/**
 * Single grid cell rectangle
 */
function GridCellRectangle({
  cell,
  onClick,
  isSelected,
}: {
  cell: GridCell;
  onClick?: () => void;
  isSelected?: boolean;
}) {
  // Skip rendering if bounds are invalid
  if (!cell.bounds || typeof cell.bounds.south !== 'number') {
    return null;
  }

  const cellBounds: LatLngBoundsExpression = [
    [cell.bounds.south, cell.bounds.west],
    [cell.bounds.north, cell.bounds.east],
  ];

  // Map backend status to frontend:
  // - 'running'/'in_progress' -> 'processing' (actively being scraped)
  // - 'queued' stays as 'queued' (waiting in BullMQ queue)
  // - Other statuses pass through unchanged
  // Note: Backend may send 'running' which needs to be mapped to 'processing' for display
  const backendStatus = cell.status as string;
  const normalizedStatus: GridCellStatus = (backendStatus === 'in_progress' || backendStatus === 'running')
    ? 'processing'
    : cell.status;
  const statusColor = CELL_STATUS_COLORS[normalizedStatus] || CELL_STATUS_COLORS.pending;

  const getOpacity = () => {
    switch (normalizedStatus) {
      case 'completed':
        return 0.4;
      case 'processing':
        return 0.6;
      case 'queued':
        return 0.45; // Between pending and processing
      case 'failed':
        return 0.5;
      case 'skipped':
        return 0.3;
      case 'pending':
      default:
        return 0.25; // Increased from 0.15 for better visibility
    }
  };

  return (
    <Rectangle
      bounds={cellBounds}
      pathOptions={{
        color: isSelected ? '#1677ff' : statusColor,
        weight: isSelected ? 3 : 1,
        fillColor: statusColor,
        fillOpacity: getOpacity(),
      }}
      eventHandlers={{
        click: onClick,
      }}
    >
      <Tooltip sticky>
        <div>
          <strong>Cell {cell.index !== undefined ? cell.index + 1 : cell.id}</strong>
          <br />
          Status: {cell.status}
          {cell.placesFound !== undefined && (
            <>
              <br />
              Places: {cell.placesFound}
            </>
          )}
          {cell.error && (
            <>
              <br />
              <span style={{ color: '#ff4d4f' }}>Error: {cell.error}</span>
            </>
          )}
        </div>
      </Tooltip>
    </Rectangle>
  );
}

export function GridMap({
  cells,
  bounds,
  loading,
  height = 400,
  onCellClick,
  selectedCellId,
}: GridMapProps) {
  const [viewport, setViewport] = useState<LatLngBounds | null>(null);

  // Check if bounds is valid
  const hasBounds = bounds && typeof bounds.north === 'number' && typeof bounds.south === 'number'
    && typeof bounds.east === 'number' && typeof bounds.west === 'number';

  const mapBounds = useMemo(
    () => hasBounds ? boundsToLeafletBounds(bounds) : null,
    [bounds, hasBounds]
  );

  const center = useMemo(
    () => hasBounds ? {
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2,
    } : { lat: 52.37, lng: 4.89 }, // Default to Amsterdam
    [bounds, hasBounds]
  );

  // Filter and limit cells for performance
  const { visibleCells, isLimited, totalCells } = useMemo(() => {
    const total = cells.length;

    // If few cells, show all
    if (total <= MAX_RENDERED_CELLS) {
      return { visibleCells: cells, isLimited: false, totalCells: total };
    }

    // Filter by viewport if available
    let filtered = viewport
      ? cells.filter(cell => cellIntersectsViewport(cell, viewport))
      : cells;

    // If still too many, prioritize and limit
    if (filtered.length > MAX_RENDERED_CELLS) {
      // Sort by priority (processing/failed first, then completed, then pending)
      const sorted = [...filtered].sort((a, b) => getCellPriority(b) - getCellPriority(a));
      filtered = sorted.slice(0, MAX_RENDERED_CELLS);
    }

    return {
      visibleCells: filtered,
      isLimited: total > MAX_RENDERED_CELLS,
      totalCells: total,
    };
  }, [cells, viewport]);

  const handleViewportChange = useCallback((newBounds: LatLngBounds | null) => {
    setViewport(newBounds);
  }, []);

  // Early return after hooks
  if (!hasBounds) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Empty description="No grid data available" />
      </div>
    );
  }

  return (
    <div>
      {/* Info banner when cells are limited - above the map */}
      {isLimited && (
        <Alert
          message={`Showing ${visibleCells.length.toLocaleString()} of ${totalCells.toLocaleString()} cells (zoom in for more detail)`}
          type="info"
          style={{ marginBottom: 8 }}
          showIcon
        />
      )}

      <div style={{ height, position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <Spin tip="Loading grid..." />
          </div>
        )}

        <MapContainer
        center={[center.lat, center.lng]}
        zoom={MAP_DEFAULTS.initialZoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
        preferCanvas={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds bounds={mapBounds} />
        <ViewportTracker onViewportChange={handleViewportChange} />

        {visibleCells.map((cell) => (
          <GridCellRectangle
            key={cell.id}
            cell={cell}
            onClick={() => onCellClick?.(cell)}
            isSelected={cell.id === selectedCellId}
          />
        ))}
        </MapContainer>
      </div>
    </div>
  );
}

/**
 * Mini grid map for previews
 */
export function GridMapMini({
  bounds,
  cellCount,
  height = 200,
}: {
  bounds: Bounds;
  cellCount: number;
  height?: number;
}) {
  const mapBounds = useMemo(
    () => boundsToLeafletBounds(bounds),
    [bounds]
  );

  const center = useMemo(
    () => ({
      lat: (bounds.north + bounds.south) / 2,
      lng: (bounds.east + bounds.west) / 2,
    }),
    [bounds]
  );

  return (
    <div style={{ height, borderRadius: 8, overflow: 'hidden' }}>
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={10}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        dragging={false}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <FitBounds bounds={mapBounds} />

        <Rectangle
          bounds={mapBounds}
          pathOptions={{
            color: '#1677ff',
            weight: 2,
            fillColor: '#1677ff',
            fillOpacity: 0.1,
          }}
        >
          <Tooltip permanent>
            {cellCount} cells
          </Tooltip>
        </Rectangle>
      </MapContainer>
    </div>
  );
}

export default GridMap;
